import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, Clock, RefreshCw, Wrench,
  User, Car, Mail, Phone, Calendar, Search, ShoppingCart, Package,
  DollarSign, FileText, CalendarDays,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import ServiceQuoteModal from '@/components/admin/ServiceQuoteModal';
import { formatDateMdY, formatDateTimeMdY } from '@/utils/dateFormat';
import { getServiceStatusLabel } from '@/constants/adminVocabulary';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_PIPELINE = [
  { value: 'pending', label: getServiceStatusLabel('pending'), short: getServiceStatusLabel('pending') },
  { value: 'scheduled', label: getServiceStatusLabel('scheduled'), short: getServiceStatusLabel('scheduled') },
  { value: 'completed', label: getServiceStatusLabel('completed'), short: getServiceStatusLabel('completed') },
];

const STATUS_COLORS = {
  pending: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
  scheduled: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  completed: 'bg-green-500/15 text-green-400 border-green-500/30',
};

const TIMELINE_EVENT_PRESENTATION = {
  service_request_created: { label: 'Service Request Created', icon: Wrench, color: 'text-blue-400' },
  service_status_changed: { label: 'Status Changed', icon: RefreshCw, color: 'text-purple-400' },
  service_quote_saved: { label: 'Quote saved', icon: DollarSign, color: 'text-emerald-400' },
  service_quote_sent: { label: 'Quote Sent to Customer', icon: Mail, color: 'text-blue-400' },
  service_quote_approved: { label: 'Quote Approved by Customer', icon: CheckCircle, color: 'text-green-400' },
  service_scheduled: { label: 'Service Scheduled', icon: CalendarDays, color: 'text-blue-400' },
};

const formatUsd = (n) => {
  const val = Number(n) || 0;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
};

const formatDate = (ds) => formatDateTimeMdY(ds);

const formatScheduledDate = (dateStr) => formatDateMdY(dateStr);

const todayDateInputValue = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getStatusLabel = (status) => getServiceStatusLabel(status);

const formatTimelineEventDetail = (event) => {
  const meta = event.metadata || {};
  if (event.event_type === 'service_status_changed' && meta.from_status && meta.to_status) {
    return `${getStatusLabel(meta.from_status)} → ${getStatusLabel(meta.to_status)}`;
  }
  if (meta.scheduled_date) return formatScheduledDate(meta.scheduled_date);
  if (meta.scheduled_date_label) return formatScheduledDate(meta.scheduled_date_label);
  if (meta.total_service_charge != null) return formatUsd(meta.total_service_charge);
  if (meta.customer_email) return meta.customer_email;
  if (meta.vehicle) return meta.vehicle;
  if (meta.customer_name) return meta.customer_name;
  return event.description || '';
};

const mapTimelineEventForDisplay = (event) => {
  const preset = TIMELINE_EVENT_PRESENTATION[event.event_type] || {
    label: (event.event_type || 'Event').replace(/_/g, ' '),
    icon: Clock,
    color: 'text-white/50',
  };
  const detail = formatTimelineEventDetail(event);
  const desc = event.description || detail;
  return {
    id: event.id,
    date: event.occurred_at,
    label: preset.label,
    desc,
    detail: detail !== desc ? detail : '',
    icon: preset.icon,
    color: preset.color,
  };
};

function InfoRow({ label, value, mono, highlight }) {
  return (
    <div className="flex justify-between items-start gap-4">
      <span className="text-xs font-['Manrope'] text-white/40 shrink-0">{label}</span>
      <span className={`text-sm font-['Manrope'] text-right ${highlight ? 'text-white font-semibold' : 'text-white/80'} ${mono ? 'font-mono text-xs' : ''}`}>
        {value || '-'}
      </span>
    </div>
  );
}

function OverviewTab({ request }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="overview-tab">
      <div className="bg-white/5 border border-white/10 p-5">
        <h3 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-4 flex items-center gap-2">
          <Wrench className="w-4 h-4 text-blue-400" />
          Service Request
        </h3>
        <div className="space-y-3">
          <InfoRow label="Customer" value={request.name} highlight />
          <InfoRow label="Status" value={getStatusLabel(request.status)} highlight />
          {request.scheduled_date && (
            <InfoRow
              label="Scheduled For"
              value={formatScheduledDate(request.scheduled_date || request.scheduled_date_label)}
              highlight
            />
          )}
          <InfoRow label="Submitted" value={formatDate(request.created_at)} />
          {request.updated_at && <InfoRow label="Last Updated" value={formatDate(request.updated_at)} />}
          <InfoRow label="Consent Given" value={request.consent ? 'Yes' : 'No'} />
          {request.service_charge?.total_service_charge != null && (
            <InfoRow
              label="Quote"
              value={formatUsd(request.service_charge.total_service_charge)}
              highlight
            />
          )}
          {request.service_charge?.status && (
            <InfoRow label="Quote status" value={request.service_charge.status.replace(/_/g, ' ')} />
          )}
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 p-5">
        <h3 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-4 flex items-center gap-2">
          <Car className="w-4 h-4 text-purple-400" />
          Vehicle
        </h3>
        <div className="space-y-3">
          <InfoRow
            label="Vehicle"
            value={`${request.vehicleYear || ''} ${request.vehicleMake || ''} ${request.vehicleModel || ''}`.trim()}
            highlight
          />
          <InfoRow label="Type" value={request.vehicleType} />
          <InfoRow label="VIN" value={request.vin} mono />
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 p-5 lg:col-span-2">
        <h3 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-4 flex items-center gap-2">
          <Package className="w-4 h-4 text-amber-400" />
          Problem Description
        </h3>
        <div className="bg-white/5 border border-white/10 p-4 text-sm font-['Manrope'] text-white/80 whitespace-pre-wrap">
          {request.problemDescription || 'No description provided.'}
        </div>
      </div>
    </div>
  );
}

function CustomerInformationTab({ request }) {
  return (
    <div className="space-y-4" data-testid="customer-information-tab">
      <div className="bg-white/5 border border-white/10 p-5">
        <h3 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-4 flex items-center gap-2">
          <User className="w-4 h-4 text-blue-400" />
          Customer Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InfoRow label="First Name" value={request.firstName} />
          <InfoRow label="Last Name" value={request.lastName} />
          <InfoRow label="Full Name" value={request.name} highlight />
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 p-5">
        <h3 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-4 flex items-center gap-2">
          <Mail className="w-4 h-4 text-green-400" />
          Contact Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex justify-between items-start gap-4">
            <span className="text-xs font-['Manrope'] text-white/40 shrink-0">Email</span>
            {request.email ? (
              <a href={`mailto:${request.email}`} className="text-sm font-['Manrope'] text-blue-400 hover:text-blue-300 text-right">
                {request.email}
              </a>
            ) : (
              <span className="text-sm font-['Manrope'] text-white/80">-</span>
            )}
          </div>
          <div className="flex justify-between items-start gap-4">
            <span className="text-xs font-['Manrope'] text-white/40 shrink-0">Phone</span>
            {request.phone ? (
              <a href={`tel:${request.phone}`} className="text-sm font-['Manrope'] text-green-400 hover:text-green-300 text-right">
                {request.phone}
              </a>
            ) : (
              <span className="text-sm font-['Manrope'] text-white/80">-</span>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 p-5">
        <h3 className="text-sm font-['Unbounded'] font-semibold text-white/80 mb-4 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-amber-400" />
          Contact Preferences
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InfoRow label="Preferred time of Contact" value={request.preferredTimeToCall} highlight />
          <InfoRow label="Preferred Service Date" value={formatScheduledDate(request.preferredServiceDate)} highlight />
        </div>
      </div>

      <div className="flex gap-3">
        {request.email && (
          <a
            href={`mailto:${request.email}`}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold transition-colors"
          >
            <Mail className="w-4 h-4" />
            Send Email
          </a>
        )}
        {request.phone && (
          <a
            href={`tel:${request.phone}`}
            className="flex items-center gap-2 px-4 py-2 border border-white/10 text-white/80 hover:bg-white/5 text-sm font-['Manrope'] transition-colors"
          >
            <Phone className="w-4 h-4" />
            Call Customer
          </a>
        )}
      </div>
    </div>
  );
}

function AutoPartsRequirementTab() {
  const handleComingSoon = (feature) => {
    toast.info(`${feature} — coming soon`);
  };

  return (
    <div data-testid="auto-parts-tab">
      <div className="bg-white/5 border border-white/10 p-8 text-center">
        <Package className="w-12 h-12 text-white/20 mx-auto mb-4" />
        <h3 className="text-base font-['Unbounded'] font-semibold text-white mb-2">
          Auto Parts Requirement
        </h3>
        <p className="text-sm font-['Manrope'] text-white/40 mb-6 max-w-md mx-auto">
          Look up parts for this service request and manage your parts cart. Backend integration will be available shortly.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => handleComingSoon('Look up parts')}
            data-testid="lookup-parts-btn"
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold transition-colors"
          >
            <Search className="w-4 h-4" />
            Look up parts
          </button>
          <button
            type="button"
            onClick={() => handleComingSoon('Your Cart')}
            data-testid="your-cart-btn"
            className="flex items-center gap-2 px-5 py-2.5 border border-white/10 text-white/80 hover:bg-white/5 text-sm font-['Manrope'] font-semibold transition-colors"
          >
            <ShoppingCart className="w-4 h-4" />
            Your Cart
          </button>
        </div>
      </div>
    </div>
  );
}

function TimelineTab({ events }) {
  const displayEvents = (events || [])
    .map(mapTimelineEventForDisplay)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div data-testid="timeline-tab">
      {displayEvents.length === 0 ? (
        <div className="bg-white/5 border border-white/10 p-12 text-center">
          <p className="text-white/40 font-['Manrope'] text-sm">No timeline events.</p>
        </div>
      ) : (
        <div className="space-y-0">
          {displayEvents.map((ev, i) => {
            const Icon = ev.icon;
            return (
              <div key={ev.id || i} className="flex gap-4 relative" data-testid={`timeline-event-${ev.id?.slice(0, 8) || i}`}>
                <div className="flex flex-col items-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-white/5 border border-white/10 shrink-0">
                    <Icon className={`w-4 h-4 ${ev.color}`} />
                  </div>
                  {i < displayEvents.length - 1 && <div className="w-px flex-1 bg-white/10 my-1" />}
                </div>
                <div className="pb-6">
                  <p className="text-sm font-['Manrope'] font-medium text-white">{ev.label}</p>
                  <p className="text-xs font-['Manrope'] text-white/40">{ev.desc}</p>
                  {ev.detail && ev.detail !== ev.desc && (
                    <p className="text-[11px] font-['Manrope'] text-white/30 mt-0.5">{ev.detail}</p>
                  )}
                  <p className="text-[11px] font-['Manrope'] text-white/25 mt-0.5">{formatDate(ev.date)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ServiceRequestDetailPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [request, setRequest] = useState(null);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [scheduleDialog, setScheduleDialog] = useState(null);

  const fetchRequest = useCallback(async () => {
    try {
      const [detailRes, timelineRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/service-requests/${requestId}`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/service-requests/${requestId}/timeline`, { headers }),
      ]);
      setRequest(detailRes.data);
      setTimelineEvents(timelineRes.data?.events || []);
    } catch (err) {
      toast.error('Failed to load service request');
      navigate('/admin/service-requests');
    } finally {
      setLoading(false);
    }
  }, [requestId, headers, navigate]);

  useEffect(() => { fetchRequest(); }, [fetchRequest]);

  const handleStatusChange = async (newStatus, scheduledDate = null) => {
    if (!request || newStatus === request.status) return;
    setStatusUpdating(true);
    try {
      const params = { new_status: newStatus };
      if (scheduledDate) params.scheduled_date = scheduledDate;
      const res = await axios.put(`${BACKEND_URL}/api/admin/service-requests/${requestId}/status`, null, {
        params,
        headers,
      });
      toast.success(res.data?.message || `Status updated to ${getStatusLabel(newStatus)}`);
      setScheduleDialog(null);
      fetchRequest();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleStatusSelect = (newStatus) => {
    if (!request || newStatus === request.status) return;
    if (newStatus === 'scheduled') {
      setScheduleDialog({
        scheduledDate: request.scheduled_date || '',
      });
      return;
    }
    handleStatusChange(newStatus);
  };

  const confirmSchedule = () => {
    if (!scheduleDialog?.scheduledDate) {
      toast.error('Please select a service date');
      return;
    }
    handleStatusChange('scheduled', scheduleDialog.scheduledDate);
  };

  if (loading) {
    return <div className="p-12 text-center text-white/40 font-['Manrope']">Loading service request...</div>;
  }
  if (!request) return null;

  const currentIdx = STATUS_PIPELINE.findIndex((s) => s.value === request.status);
  const statusColor = STATUS_COLORS[request.status] || STATUS_COLORS.pending;

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto" data-testid="service-request-detail-page">
      <div className="flex items-center gap-2 mb-4 text-sm font-['Manrope']">
        <button
          onClick={() => navigate('/admin/service-requests')}
          className="text-white/40 hover:text-white transition-colors flex items-center gap-1"
          data-testid="back-to-service-requests"
        >
          <ArrowLeft className="w-4 h-4" />
          Service Requests
        </button>
      </div>

      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl font-['Unbounded'] font-bold text-white mb-1" data-testid="service-request-customer-name">
            {request.name}
          </h1>
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`px-3 py-1 text-xs font-['Manrope'] font-medium border ${statusColor}`} data-testid="service-request-status-badge">
              {getStatusLabel(request.status)}
            </span>
            {request.scheduled_date && (
              <span className="text-xs font-['Manrope'] text-blue-300/80 flex items-center gap-1">
                <CalendarDays className="w-3 h-3" />
                {formatScheduledDate(request.scheduled_date || request.scheduled_date_label)}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <select
            value={request.status}
            onChange={(e) => handleStatusSelect(e.target.value)}
            disabled={statusUpdating}
            data-testid="service-request-status-select"
            className="bg-black border border-white/10 text-white text-sm px-3 py-2 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [&>option]:bg-gray-900 [&>option]:text-white"
          >
            {STATUS_PIPELINE.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setShowQuoteModal(true)}
            data-testid="add-service-quote-btn"
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white text-sm font-['Manrope'] font-semibold transition-colors"
          >
            <FileText className="w-4 h-4" />
            Add Service Quote
          </button>
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 p-4 mb-6 overflow-x-auto" data-testid="status-pipeline">
        <div className="flex items-center min-w-[400px]">
          {STATUS_PIPELINE.map((step, i) => {
            const isCompleted = i < currentIdx;
            const isCurrent = i === currentIdx;
            return (
              <React.Fragment key={step.value}>
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className={`w-8 h-8 flex items-center justify-center text-xs font-['Manrope'] font-bold border-2 transition-all ${
                    isCurrent ? 'bg-blue-500 border-blue-500 text-white' :
                    isCompleted ? 'bg-green-500/20 border-green-500 text-green-400' :
                    'bg-white/5 border-white/20 text-white/30'
                  }`}>
                    {isCompleted ? <CheckCircle className="w-4 h-4" /> : i + 1}
                  </div>
                  <span className={`text-[10px] font-['Manrope'] mt-1 ${isCurrent ? 'text-blue-400' : isCompleted ? 'text-green-400/60' : 'text-white/20'}`}>
                    {step.short}
                  </span>
                </div>
                {i < STATUS_PIPELINE.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-1 ${isCompleted ? 'bg-green-500/40' : 'bg-white/10'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="flex gap-0 mb-6 border-b border-white/10 overflow-x-auto">
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'customer-information', label: 'Customer Information' },
          { id: 'auto-parts', label: 'Auto Parts Requirement' },
          { id: 'timeline', label: 'Timeline' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`tab-${tab.id}`}
            className={`px-5 py-2.5 text-sm font-['Manrope'] font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'text-blue-400 border-blue-400'
                : 'text-white/40 border-transparent hover:text-white/60'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && <OverviewTab request={request} />}
      {activeTab === 'customer-information' && <CustomerInformationTab request={request} />}
      {activeTab === 'auto-parts' && <AutoPartsRequirementTab />}
      {activeTab === 'timeline' && <TimelineTab events={timelineEvents} />}

      {showQuoteModal && (
        <ServiceQuoteModal
          requestId={requestId}
          request={request}
          token={token}
          onClose={() => setShowQuoteModal(false)}
          onSuccess={fetchRequest}
        />
      )}

      {scheduleDialog && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
          data-testid="schedule-service-dialog"
        >
          <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-md p-5">
            <div className="flex items-center gap-2 mb-2">
              <CalendarDays className="w-5 h-5 text-blue-400" />
              <h3 className="text-base font-['Unbounded'] font-semibold text-white">Schedule Service</h3>
            </div>
            <p className="text-sm font-['Manrope'] text-white/60 mb-4">
              Select the date this service is scheduled for {request.name}.
            </p>
            <label className="block text-xs font-['Manrope'] text-white/60 mb-1.5">
              Service Date *
            </label>
            <input
              type="date"
              value={scheduleDialog.scheduledDate}
              min={todayDateInputValue()}
              onChange={(e) => setScheduleDialog({ scheduledDate: e.target.value })}
              data-testid="schedule-date-input"
              className="w-full bg-black border border-white/10 text-white text-sm px-3 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50 [color-scheme:dark]"
            />
            {scheduleDialog.scheduledDate && (
              <p className="text-xs font-['Manrope'] text-blue-300/80 mt-2">
                Service Scheduled for {formatScheduledDate(scheduleDialog.scheduledDate)}
              </p>
            )}
            <div className="flex justify-end gap-3 mt-5">
              <button
                type="button"
                onClick={() => setScheduleDialog(null)}
                disabled={statusUpdating}
                className="px-4 py-2 border border-white/10 text-white/70 text-sm font-['Manrope'] hover:bg-white/5 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmSchedule}
                disabled={statusUpdating || !scheduleDialog.scheduledDate}
                data-testid="confirm-schedule-btn"
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-['Manrope'] font-semibold disabled:opacity-50"
              >
                {statusUpdating ? 'Saving...' : 'Confirm Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}